import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Phone, Calendar, LogOut, Sun, Moon, List, Upload, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const Sidebar = ({ user, onLogout, theme, onToggleTheme, collapsed }) => {
  const location = useLocation();
  const { language, t, toggleLanguage } = useLanguage();

  const menuItems = [
    { path: '/', icon: Home, label: t('dashboard'), testId: 'nav-dashboard' },
    { path: '/leads', icon: Phone, label: t('leads'), testId: 'nav-leads' },
    { path: '/campaigns', icon: List, label: t('campaigns'), testId: 'nav-campaigns' },
    { path: '/calendar', icon: Calendar, label: t('calendar'), testId: 'nav-calendar' },
  ];

  if (user.role === 'admin') {
    menuItems.push({ path: '/import', icon: Upload, label: t('import_leads') || 'Importa Lead', testId: 'nav-import' });
    menuItems.push({ path: '/users', icon: Users, label: t('users'), testId: 'nav-users' });
  }
  // Security page (2FA + own password) is available to every authenticated user; the Danger Zone is admin-gated inside the page.
  menuItems.push({ path: '/security', icon: Shield, label: t('sec_nav') || 'Sicurezza', testId: 'nav-security' });

  return (
    <div
      className={`flex flex-col h-screen border-r transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
      style={{
        backgroundColor: theme === 'dark' ? '#0A0A0A' : '#FFFFFF',
        borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
      }}
    >
      <div className="p-4 border-b" style={{ borderColor: theme === 'dark' ? '#262626' : '#E0E0E0' }}>
        {!collapsed && (
          <h2 className="text-xl font-bold" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
            CRM Outbound
          </h2>
        )}
      </div>

      <nav className="flex-1 p-2 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                collapsed ? 'justify-center' : ''
              }`}
              style={{
                backgroundColor: isActive
                  ? theme === 'dark'
                    ? '#161616'
                    : '#E8EAED'
                  : 'transparent',
                color: isActive ? '#0F62FE' : theme === 'dark' ? '#A8A8A8' : '#525252',
              }}
            >
              <Icon size={20} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="p-2 border-t space-y-2" style={{ borderColor: theme === 'dark' ? '#262626' : '#E0E0E0' }}>
        {!collapsed && (
          <div
            className="flex items-center justify-center gap-2 p-2 rounded-md"
            style={{ backgroundColor: theme === 'dark' ? '#161616' : '#F8F9FA' }}
            data-testid="language-switcher"
          >
            <button
              onClick={() => toggleLanguage('it')}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${
                language === 'it' ? 'ring-2 ring-blue-500' : 'opacity-50 hover:opacity-100'
              }`}
              title="Italiano"
              data-testid="lang-it-button"
            >
              <span className="text-2xl">🇮🇹</span>
              <span className="text-xs font-medium" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                IT
              </span>
            </button>
            <button
              onClick={() => toggleLanguage('en')}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${
                language === 'en' ? 'ring-2 ring-blue-500' : 'opacity-50 hover:opacity-100'
              }`}
              title="English"
              data-testid="lang-en-button"
            >
              <span className="text-2xl">🇬🇧</span>
              <span className="text-xs font-medium" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                EN
              </span>
            </button>
          </div>
        )}
        {collapsed && (
          <div className="flex flex-col items-center gap-1" data-testid="language-switcher">
            <button
              onClick={() => toggleLanguage('it')}
              className={`text-xl transition-all ${language === 'it' ? '' : 'opacity-50'}`}
              title="Italiano"
              data-testid="lang-it-button"
            >
              🇮🇹
            </button>
            <button
              onClick={() => toggleLanguage('en')}
              className={`text-xl transition-all ${language === 'en' ? '' : 'opacity-50'}`}
              title="English"
              data-testid="lang-en-button"
            >
              🇬🇧
            </button>
          </div>
        )}

        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={onToggleTheme}
          data-testid="theme-toggle-button"
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          {!collapsed && <span className="ml-3">{theme === 'dark' ? t('light') : t('dark')}</span>}
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={onLogout}
          data-testid="logout-button"
        >
          <LogOut size={20} />
          {!collapsed && <span className="ml-3">{t('logout')}</span>}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
